/* MENASA chapter product page (template product.chapter): the card stack and the four card components.
   Each card section renders one custom element (mc-piece, mc-details, mc-figure, mc-house). Elements set themselves
   up when connected and remove every listener, observer and portalled overlay when disconnected, so theme-editor
   re-renders and the theme's page transitions leave nothing behind. Built from prototype/product-page. */
(() => {
  if (window.mcChapter) return;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const pad = n => String(n).padStart(2, '0');
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const reduceMQ = matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = () => reduceMQ.matches;
  const coarseMQ = matchMedia('(pointer: coarse)');
  const coarse = () => coarseMQ.matches;
  const hasTimeline = CSS.supports('animation-timeline: view()');
  if (/[?&]jsstack/.test(location.search)) document.documentElement.classList.add('mc-js-stack');
  const cssStack = () => CSS.supports('timeline-scope: --a') && !document.documentElement.classList.contains('mc-js-stack');

  /* ---------- generated geometry ---------- */
  const P = (r, a) => [r * Math.cos(a), r * Math.sin(a)].map(n => n.toFixed(2));
  const RING = (() => {
    let s = '<circle r="48"/><circle r="46.6" class="soft"/><circle r="30" class="soft"/>';
    for (let k = 0; k < 3; k++) s += `<polygon points="${[0, 1, 2, 3].map(i => P(46.6, (i * 90 + k * 30) * Math.PI / 180).join(',')).join(' ')}"/>`;
    for (let k = 0; k < 2; k++) s += `<polygon class="soft" points="${[0, 1, 2, 3, 4, 5].map(i => P(46.6, (i * 60 + k * 30 + 15) * Math.PI / 180).join(',')).join(' ')}"/>`;
    s += `<polygon class="soft" points="${Array.from({ length: 24 }, (_, i) => P(i % 2 ? 26 : 30, i * Math.PI / 12).join(',')).join(' ')}"/>`;
    for (let i = 0; i < 96; i++) { const a = i * Math.PI / 48, [x1, y1] = P(46.6, a), [x2, y2] = P(i % 8 ? 47.4 : 48, a); s += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"/>`; }
    return s;
  })();
  const STAR = Array.from({ length: 16 }, (_, i) => P(i % 2 ? 16.5 : 22, i * Math.PI / 8 - Math.PI / 2).map(n => (+n + 50).toFixed(2)).join(',')).join(' ');

  /* ---------- reveals ---------- */
  function prepare(root) {
    $$('[data-ring]', root).forEach(svg => { if (!svg.childElementCount) svg.innerHTML = RING; });
    $$('[data-star]', root).forEach(svg => { if (!svg.childElementCount) svg.innerHTML = `<polygon points="${STAR}"/><circle class="core" cx="50" cy="50" r="6"/>`; });
    $$('[data-split]', root).forEach(el => {
      if (el.dataset.splitDone) return;
      el.dataset.splitDone = '1';
      let i = 0;
      const label = el.textContent.replace(/\s+/g, ' ').trim();
      el.innerHTML = el.innerHTML.split(/(<br\s*\/?>)/i).map(part => /^<br/i.test(part) ? part :
        part.split(/(\s+)/).map(w => !w || /^\s+$/.test(w) ? w : `<span class="mc-w" aria-hidden="true"><span style="--i:${i++}">${w}</span></span>`).join('')).join('');
      el.setAttribute('aria-label', label);
    });
    // Page travel belongs to the shared router. Only the gallery ring reveals;
    // headings, purchase controls and garments are already in their final state.
    $$('[data-reveal],[data-split],[data-io]', root).forEach(el => el.classList.add('in'));
  }

  /* ---------- base component: every card cleans up after itself ---------- */
  class Card extends HTMLElement {
    connectedCallback() {
      this._off = [];
      prepare(this);
      try { this.setup(); } catch (e) { console.error('[mc]', e); }
      Stack.refresh();
    }
    disconnectedCallback() {
      (this._off || []).forEach(f => { try { f(); } catch {} });
      this._off = [];
      Stack.refresh();
    }
    on(t, type, fn, o) { t.addEventListener(type, fn, o); this._off.push(() => t.removeEventListener(type, fn, o)); }
    observe(Ctor, cb, targets, opts) { const o = new Ctor(cb, opts); [].concat(targets).forEach(t => t && o.observe(t)); this._off.push(() => o.disconnect()); return o; }
    pauseOff(watch, targets) { this.observe(IntersectionObserver, ([e]) => targets.forEach(t => t && (t.style.animationPlayState = e.isIntersecting ? '' : 'paused')), watch); }
    // overlays are moved to <body> so a covered (scaled) card cannot carry them along
    portal(el) { if (!el) return el; document.body.appendChild(el); this._off.push(() => el.remove()); return el; }
    setup() {}
  }

  /* ================= the stack =================
     Cards are the section wrappers (.mc-card-section) directly inside #MainContent. Sticky tops come from CSS with the
     measured height; covering runs on CSS scroll timelines where supported, else on this frame loop (same numbers). */
  const Stack = {
    cards: [], on: false, off: [], raf: 0,
    refresh() { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.collect()); },
    collect() {
      const main = document.getElementById('MainContent');
      const cards = main ? $$(':scope > .mc-card-section', main).filter(w => $('.mc-card', w)) : [];
      if (!cards.length) return this.stop();
      cards.forEach((w, i) => {
        w.dataset.mcPos = i + 1;
        const card = $('.mc-card', w);
        const [bg, ink] = (card.dataset.tone || '250,247,239|33,27,23').split('|');
        w._bg = bg.split(',').map(Number); w._ink = ink.split(',').map(Number);
        w._shade = $('.mc-card__shade', w); w._next = $('.mc-card__next', w);
        if (w._next && !w._next._mc) { w._next._mc = 1; w._next.addEventListener('click', e => { e.preventDefault(); this.go(w); }); }
        $$('[data-mc-jump]', w).forEach(b => {
          b.hidden = i === cards.length - 1;
          if (!b._mc) { b._mc = 1; b.addEventListener('click', () => this.go(w)); }
        });
      });
      this.cards = cards;
      if (!this.on) this.start();
      this.ro.disconnect(); cards.forEach(c => this.ro.observe(c)); this.ro.observe(main);
      this.measure();
    },
    start() {
      this.on = true;
      this.hdr = document.getElementById('PrestigeHeader');
      this.ro = new ResizeObserver(() => this.measure());
      const add = (t, ty, fn, o) => { t.addEventListener(ty, fn, o); this.off.push(() => t.removeEventListener(ty, fn, o)); };
      this.vh = innerHeight; this.dir = 1; this.lastY = scrollY; this.touching = false; this.idle = 0; this.anim = 0;
      this.wRaf = 0; this.wLast = 0; this.wTarget = this.wCur = scrollY; this.key = '';
      add(window, 'scroll', () => this.onScroll(), { passive: true });
      add(window, 'resize', () => { if (innerHeight !== this.vh) { this.vh = innerHeight; this.tick(); } });
      if ('onscrollend' in window) add(window, 'scrollend', () => { if (!this.anim && !this.wRaf) this.settle(); });
      add(window, 'touchstart', () => { this.touching = true; this.stopGlide(); }, { passive: true });
      const lift = () => { this.touching = false; clearTimeout(this.idle); this.idle = setTimeout(() => this.settle(), 160); };
      add(window, 'touchend', lift, { passive: true }); add(window, 'touchcancel', lift, { passive: true });
      add(window, 'keydown', e => { if (/Arrow|Page|Home|End|Space/.test(e.code)) this.stopGlide(); });
      add(window, 'wheel', e => this.onWheel(e), { passive: false });
      add(document, 'shopify:section:load', () => this.refresh());
      add(document, 'shopify:section:unload', () => this.refresh());
      add(document, 'shopify:section:reorder', () => this.refresh());
      document.documentElement.classList.add('mc-page');
    },
    stop() {
      if (!this.on) return;
      this.on = false;
      this.off.forEach(f => f()); this.off = [];
      this.ro && this.ro.disconnect();
      this.stopGlide(); this.stopWheel(); clearTimeout(this.idle);
      if (this.hdr) ['--header-bg', '--header-text', '--header-border', '--nav-rgb', '--nav-ink', '--header-top', '--header-bottom'].forEach(n => this.hdr.style.removeProperty(n));
      document.documentElement.classList.remove('mc-page');
      this.cards = [];
    },
    measure() {
      if (!this.cards.length) return;
      // natural top of the first card: the main element's top plus whatever sits before the cards (never sticky)
      const main = this.cards[0].parentElement;
      let y = main.getBoundingClientRect().top + scrollY + (parseFloat(getComputedStyle(main).paddingTop) || 0);
      for (let el = main.firstElementChild; el && el !== this.cards[0]; el = el.nextElementSibling) y += el.offsetHeight;
      // phones overlap each card onto the one before by the peek (a negative top margin), so count the margin in
      this.cards.forEach((c, i) => { const h = c.offsetHeight; if (i) y += parseFloat(getComputedStyle(c).marginTop) || 0; c._top = y; if (h !== c._h) { c._h = h; c.style.setProperty('--h', h + 'px'); } y += h; });
      this.end = y; this.hh = this.hdr ? this.hdr.offsetHeight : 0;
      this.tick();
    },
    visTop(c, y) { return Math.max(Math.min(0, this.vh - c._h), c._top - y); },
    tick() { if (!this.ticking) { this.ticking = true; requestAnimationFrame(() => this.frame()); } },
    frame() {
      this.ticking = false;
      const y = scrollY, cards = this.cards, vh = this.vh;
      if (!cards.length) return;
      // header colour: blend into each card's tone as its edge passes under the header
      const mix = (a, b, t) => a.map((v, k) => v + (b[k] - v) * t);
      let bg = cards[0]._bg, ink = cards[0]._ink;
      const docEnd = document.documentElement.scrollHeight - vh;
      for (let i = 1; i < cards.length; i++) {
        const short = Math.max(0, cards[i]._top - docEnd);
        const t = clamp(1 - Math.max(0, this.visTop(cards[i], y) - short) / ((this.hh || 72) * 1.6), 0, 1);
        if (!t) break;
        bg = mix(bg, cards[i]._bg, t); ink = mix(ink, cards[i]._ink, t);
      }
      // past the last card (footer) the theme's own header colours return
      const past = cards[cards.length - 1]._top + cards[cards.length - 1]._h - y < (this.hh || 72);
      const key = past ? 'x' : bg.join() + ink.join();
      if (this.hdr && key !== this.key) {
        this.key = key;
        const s = this.hdr.style;
        if (past) ['--header-bg', '--header-text', '--header-border', '--nav-rgb', '--nav-ink', '--header-top', '--header-bottom'].forEach(n => s.removeProperty(n));
        else {
          s.setProperty('--header-bg', `rgb(${bg.map(Math.round)})`);
          s.setProperty('--header-text', `rgb(${ink.map(Math.round)})`);
          s.setProperty('--header-border', `rgba(${ink.map(Math.round)},.12)`);
          // the theme's glass header (artistic transitions) paints from these, as on the homepage
          s.setProperty('--nav-rgb', bg.map(Math.round).join(' '));
          s.setProperty('--nav-ink', ink.map(Math.round).join(' '));
          s.setProperty('--header-top', '.97'); s.setProperty('--header-bottom', '.95');
        }
      }
      if (!cssStack() && !reduce()) {
        const soft = innerWidth < 1100;
        for (let i = 1; i < cards.length; i++) {
          const c = cards[i], p = clamp((vh - this.visTop(c, y)) / Math.min(c._h || vh, vh), 0, 1);
          if (p === c._p) continue;
          c._p = p;
          const prev = cards[i - 1], ps = clamp((p - (soft ? .25 : .2)) / (soft ? .75 : .8), 0, 1), pd = clamp((p - (soft ? .3 : .25)) / (soft ? .7 : .75), 0, 1);
          prev.style.transform = ps ? `scale(${(1 - (soft ? .025 : .045) * ps).toFixed(4)})` : '';
          if (prev._shade) prev._shade.style.opacity = ((soft ? .4 : .5) * pd).toFixed(3);
          if (prev._next) prev._next.style.opacity = clamp(1 - p / .4, 0, 1).toFixed(3);
          // the pull's resistance only makes sense with the settle; on touch the card follows the finger exactly
          c.style.translate = coarse() ? '' : `0 ${(pullAt(p) * vh).toFixed(1)}px`;
        }
      }
    },
    onScroll() {
      this.tick();
      const y = scrollY;
      if (!this.anim && Math.abs(y - this.lastY) > .5) this.dir = y > this.lastY ? 1 : -1;
      if (this.wRaf) { this.lastY = y; return; }
      this.lastY = y;
      if (!this.anim) { clearTimeout(this.idle); this.idle = setTimeout(() => this.settle(), 'onscrollend' in window ? 400 : 160); }
    },
    locked() { return document.documentElement.style.overflow === 'hidden' || document.body.style.overflow === 'hidden' || !!$('.mc-lb.on,cart-drawer.active,cart-drawer[open],.mobile-menu[aria-hidden="false"]'); },
    // to the card after w: the browser's own smooth scroll on touch (it cooperates with momentum), the glide elsewhere
    go(w) {
      const next = this.cards[this.cards.indexOf(w) + 1];
      if (!next) return;
      if (coarse()) scrollTo({ top: next._top, behavior: reduce() ? 'auto' : 'smooth' });
      else this.glide(next._top, 900);
    },
    stopGlide() { if (this.anim) { cancelAnimationFrame(this.anim); this.anim = 0; } },
    stopWheel() { if (this.wRaf) { cancelAnimationFrame(this.wRaf); this.wRaf = 0; this.wLast = 0; } },
    glide(to, dur, spring) {
      this.stopGlide(); this.stopWheel();
      to = clamp(Math.round(to), 0, document.documentElement.scrollHeight - innerHeight);
      const from = scrollY;
      if (reduce() || Math.abs(to - from) < 2) { scrollTo(0, to); return; }
      const t0 = performance.now();
      const ease = spring ? t => 1 - Math.pow(1 - t, 3) : t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const step = now => { const t = Math.min(1, (now - t0) / dur); scrollTo(0, from + (to - from) * ease(t)); this.anim = t < 1 ? requestAnimationFrame(step) : 0; };
      this.anim = requestAnimationFrame(step);
    },
    // never rest half-way through a cover: a short pull springs back, past the threshold the cover completes
    settle() {
      clearTimeout(this.idle);
      if (reduce() || this.touching || this.anim || this.wRaf || this.locked()) return;
      // Touch screens keep their own momentum. Settling after the finger lifted scripted the scroll while iOS was still
      // gliding, and the two fought (9 Oct 2026: cards "appear and settle" laggy and buggy on iPhone). The peek between
      // cards does the guiding instead.
      if (coarse()) return;
      const y = scrollY, vh = this.vh, max = document.documentElement.scrollHeight - innerHeight;
      for (let i = 1; i < this.cards.length; i++) {
        const a = this.cards[i]._top - vh, b = Math.min(this.cards[i]._top, max);
        if (y <= a + 2 || y >= b - 2) continue;
        const p = (y - a) / (b - a);
        const to = this.dir > 0 ? (p >= PULL ? b : a) : (p <= 1 - PULL ? a : b);
        const back = (to === a) === (this.dir > 0);
        this.glide(to, back ? clamp(380 + 700 * Math.abs(to - y) / vh, 380, 620) : clamp(520 + 520 * Math.abs(to - y) / vh, 560, 1040), back);
        return;
      }
    },
    // smooth wheel: mouse and trackpad glide instead of stepping (touch keeps native scrolling)
    onWheel(e) {
      this.stopGlide();
      if (!matchMedia('(pointer: fine)').matches || reduce() || e.ctrlKey || this.locked()) return;
      // Macs and trackpads keep the browser's own scrolling: their momentum is already smooth, and gliding on top of it
      // fought the inertia (9 Oct 2026, the product page felt broken on a Mac). Only a notched mouse wheel glides.
      if (window.MenasaQuality?.mac || (e.deltaMode === 0 && (Math.abs(e.deltaY) < 40 || !Number.isInteger(e.deltaY)))) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.target.closest('.mc-lb,.mc-sheet,.mc-mini,.mc-drawer,cart-drawer,.cart-drawer,#MobileMenu,dialog,[role="dialog"]')) return;
      e.preventDefault();
      if (!this.wRaf) this.wTarget = this.wCur = scrollY;
      const d = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * innerHeight : e.deltaY;
      this.wTarget = clamp(this.wTarget + d, 0, document.documentElement.scrollHeight - innerHeight);
      if (!this.wRaf) this.wRaf = requestAnimationFrame(now => this.wheelStep(now));
    },
    wheelStep(now) {
      const dt = Math.min(48, now - (this.wLast || now - 16)); this.wLast = now;
      this.wCur += (this.wTarget - this.wCur) * (1 - Math.exp(-dt / 105));
      if (Math.abs(this.wTarget - this.wCur) < .5) this.wCur = this.wTarget;
      scrollTo(0, this.wCur);
      if (this.wCur !== this.wTarget) this.wRaf = requestAnimationFrame(n => this.wheelStep(n));
      else { this.wRaf = 0; this.wLast = 0; clearTimeout(this.idle); this.idle = setTimeout(() => this.settle(), 140); }
    }
  };
  // resistance at both ends of a cover, as a fraction of the viewport (matches @keyframes pull in mc-chapter.css)
  const PULL = 0.28, sstep = x => x * x * (3 - 2 * x);
  const pullAt = p => p < PULL ? p * 0.72 * (1 - sstep(p / PULL)) : p > 1 - PULL ? -(1 - p) * 0.72 * (1 - sstep((1 - p) / PULL)) : 0;

  /* ================= card 1: the piece — gallery, viewer, choosing, adding to the bag ================= */
  class Piece extends Card {
    setup() {
      const root = this, reduceNow = reduce();
      const q = s => $(`[data-mc-${s}]`, root);
      const flightImages = $$('[data-mc-mbar] img,[data-mc-mini] .mc-mini__img img', root);
      const data = JSON.parse(q('product')?.textContent || '{}');
      this.data = data;

      /* ---------- gallery ---------- */
      const sc = q('scroller'), allSlides = $$('.mc-slide', sc), allThumbs = $$('.mc-thumb', root);
      if (!allSlides.length) return;
      let slides = allSlides.filter(s => !s.hidden), thumbs = allThumbs.filter(t => !t.hidden), N = slides.length;
      const thumbRing = q('thumbring'), galLine = q('galline'), countNow = q('countnow');
      const prevB = q('prev'), nextB = q('next'), galRing = q('galring');
      let index = -1, W = sc.clientWidth || 1, anim = 0, sraf = 0;
      galLine.style.width = 100 / N + '%';
      q('countall').textContent = '/ ' + pad(N);
      const easeOut = t => 1 - Math.pow(1 - t, 4);
      const glideTo = (i, dur) => {
        i = clamp(i, 0, N - 1);
        const from = sc.scrollLeft, to = slides[i].offsetLeft;
        cancelAnimationFrame(anim);
        if (reduce() || Math.abs(to - from) < 1) { sc.scrollLeft = to; return; }
        dur = dur || Math.min(1100, 620 + Math.abs(to - from) / W * 140);
        sc.classList.add('free');
        const t0 = performance.now();
        const step = now => { const t = Math.min(1, (now - t0) / dur); sc.scrollLeft = from + (to - from) * easeOut(t); if (t < 1) anim = requestAnimationFrame(step); else { anim = 0; sc.classList.remove('free'); } };
        anim = requestAnimationFrame(step);
      };
      const setIndex = i => {
        if (i === index) return;
        const dir = i > index ? 1 : -1;
        index = i;
        thumbs.forEach((t, k) => { t.classList.toggle('on', k === i); t.setAttribute('aria-selected', k === i); t.tabIndex = k === i ? 0 : -1; });
        prevB.disabled = i === 0; nextB.disabled = i === N - 1;
        if (!reduce() && countNow.textContent) countNow.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${-100 * dir}%)`, opacity: 0 }], { duration: 180, easing: 'ease-in' }).onfinish = () => {
          if (index !== i) return;
          countNow.textContent = pad(i + 1);
          countNow.animate([{ transform: `translateY(${100 * dir}%)`, opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 380, easing: 'cubic-bezier(.16,1,.3,1)' });
        }; else countNow.textContent = pad(i + 1);
        const th = thumbs[i];
        if (th && th.offsetParent) { const box = th.parentElement; if (th.offsetLeft < box.scrollLeft || th.offsetLeft + th.offsetWidth > box.scrollLeft + box.clientWidth) box.scrollTo({ left: th.offsetLeft - 20, behavior: reduce() ? 'instant' : 'smooth' }); }
      };
      const onGalScroll = () => {
        sraf = 0;
        const p = sc.scrollLeft / W;
        galLine.style.transform = `translate3d(${(p * 100).toFixed(2)}%,0,0)`;
        const a = clamp(Math.floor(p), 0, N - 1), b = clamp(a + 1, 0, N - 1), f = clamp(p - a, 0, 1), ta = thumbs[a], tb = thumbs[b];
        if (ta && tb && ta.offsetParent) { thumbRing.style.transform = `translate3d(${ta.offsetLeft + (tb.offsetLeft - ta.offsetLeft) * f - 3}px,0,0)`; thumbRing.style.width = ta.offsetWidth + 6 + 'px'; }
        if (!hasTimeline) slides.forEach((s, k) => {
          const rel = k - p; if (Math.abs(rel) > 1.3) return;
          const inner = s.firstElementChild, a2 = Math.min(Math.abs(rel), 1);
          inner.style.transform = `translate3d(${-rel * 34}%,0,0) scale(${1 - a2 * .12})`; inner.style.opacity = clamp(1 - a2 * 1.6, 0, 1);
          s.lastElementChild.style.opacity = 1 - Math.min(1, a2 * 4);
        });
        setIndex(clamp(Math.round(p), 0, N - 1));
      };
      this.on(sc, 'scroll', () => { if (!sraf) sraf = requestAnimationFrame(onGalScroll); }, { passive: true });
      prevB.onclick = () => glideTo(index - 1);
      nextB.onclick = () => glideTo(index + 1);
      allThumbs.forEach(t => {
        t.onclick = () => { const i = thumbs.indexOf(t); if (i >= 0) glideTo(i); };
        this.on(t, 'keydown', e => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
          e.preventDefault();
          const i = e.key === 'Home' ? 0 : e.key === 'End' ? N - 1 : clamp(thumbs.indexOf(t) + (e.key === 'ArrowRight' ? 1 : -1), 0, N - 1);
          glideTo(i); thumbs[i].focus({ preventScroll: true });
        });
      });
      this.on(sc, 'keydown', e => {
        if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) { e.preventDefault(); glideTo(e.key === 'Home' ? 0 : e.key === 'End' ? N - 1 : index + (e.key === 'ArrowRight' ? 1 : -1)); }
        if (e.key === 'Enter') openLb();
      });
      let drag = null;
      this.on(sc, 'pointerdown', e => { if (e.pointerType !== 'mouse' || e.button !== 0) return; cancelAnimationFrame(anim); drag = { x: e.clientX, s: sc.scrollLeft, lx: e.clientX, lt: performance.now(), v: 0, moved: false, id: e.pointerId }; });
      this.on(sc, 'pointermove', e => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) > 4) { drag.moved = true; sc.classList.add('free', 'grabbing'); try { sc.setPointerCapture(drag.id); } catch {} }
        if (!drag.moved) return;
        const now = performance.now(), dt = Math.max(1, now - drag.lt);
        drag.v = drag.v * .7 + ((e.clientX - drag.lx) / dt) * .3; drag.lx = e.clientX; drag.lt = now;
        sc.scrollLeft = drag.s - dx;
      });
      const endDrag = () => {
        if (!drag) return;
        const d = drag; drag = null;
        sc.classList.remove('grabbing');
        if (!d.moved) return;
        sc.dataset.dragged = '1'; setTimeout(() => delete sc.dataset.dragged, 60);
        glideTo(clamp(Math.round((sc.scrollLeft - d.v * 240) / W), index - 1, index + 1), 700);
      };
      this.on(sc, 'pointerup', endDrag); this.on(sc, 'pointercancel', endDrag);
      this.on(sc, 'click', () => { if (!sc.dataset.dragged) openLb(); });
      this.observe(ResizeObserver, () => { W = sc.clientWidth || 1; sc.classList.add('free'); sc.scrollLeft = slides[Math.max(0, index)].offsetLeft; sc.classList.remove('free'); onGalScroll(); }, sc);
      setIndex(0); onGalScroll();

      /* ---------- full-screen viewer ---------- */
      const lb = this.portal(q('lb')), lbTrack = $('.mc-lb__track', lb), lbBg = $('.mc-lb__bg', lb), lbThumbs = $('.mc-lb__thumbs', lb), lbHint = $('.mc-lb__hint', lb), lbCount = $('.mc-lb__count', lb);
      const lbPrev = $('.mc-lb__side.prev', lb), lbNext = $('.mc-lb__side.mc-next', lb), lbX = $('.mc-x', lb);
      const allVS = allSlides.map(s => { const img = $('img', s); return { src: img.currentSrc || img.getAttribute('src'), z: img.dataset.z || img.getAttribute('src'), alt: img.alt, img, cut: img.classList.contains('mc-cutout'), el: img.closest('.mc-arch') || img }; });
      lbTrack.innerHTML = allVS.map((v, i) => `<div class="mc-lb__slide"${allSlides[i].hidden ? ' hidden' : ''}><div class="mc-lb__pz"><img src="${v.img.getAttribute('src')}" alt="${v.alt.replace(/"/g, '&quot;')}" draggable="false"></div></div>`).join('');
      lbThumbs.innerHTML = allVS.map((v, i) => `<button type="button" class="${v.cut ? 'mc-cut' : ''}"${allSlides[i].hidden ? ' hidden' : ''} aria-label="Image ${i + 1}"><img src="${v.img.getAttribute('src')}" alt=""></button>`).join('');
      const allLbS = $$('.mc-lb__slide', lbTrack), allLbPZ = $$('.mc-lb__pz', lbTrack), allLbTh = $$('button', lbThumbs);
      let VS = allVS.filter((v, i) => !allSlides[i].hidden), lbS = allLbS.filter(s => !s.hidden), lbPZ = allLbPZ.filter((p, i) => !allSlides[i].hidden), lbTh = allLbTh.filter(t => !t.hidden);
      const MAXZ = 3, fineVP = matchMedia('(hover:hover) and (pointer:fine)');
      const vEaseOut = t => 1 - Math.pow(1 - t, 3);
      let vi = 0, lw = innerWidth, lh = innerHeight, boxes = [], zs = 1, zx = 0, zy = 0, trackX = 0, vOpen = false, vAnim = 0, dismissY = 0, hintT = 0;
      const vTween = (dur, step, done) => {
        cancelAnimationFrame(vAnim);
        if (reduce() || dur <= 0) { step(1); vAnim = 0; done && done(); return; }
        const t0 = performance.now();
        const f = now => { const t = Math.min(1, (now - t0) / dur); step(vEaseOut(t)); if (t < 1) vAnim = requestAnimationFrame(f); else { vAnim = 0; done && done(); } };
        vAnim = requestAnimationFrame(f);
      };
      const dims = v => ({ w: v.img.naturalWidth || +v.img.getAttribute('width') || 1000, h: v.img.naturalHeight || +v.img.getAttribute('height') || 1000 });
      const setTrack = x => { trackX = x; lbTrack.style.transform = `translate3d(${x}px,0,0)`; };
      const vLayout = () => {
        lw = innerWidth; lh = innerHeight;
        const phone = lw < 700, padX = phone ? 0 : Math.min(96, lw * .07), padY = phone ? 64 : 84;
        boxes = VS.map(v => { const d = dims(v), k = Math.min((lw - padX * 2) / d.w, (lh - padY * 2) / d.h); return { w: d.w * k, h: d.h * k }; });
        lbPZ.forEach((p, i) => { p.style.width = boxes[i].w + 'px'; p.style.height = boxes[i].h + 'px'; });
        setTrack(-vi * lw);
      };
      const setZ = (k, x, y) => { zs = k; zx = x; zy = y; lbPZ[vi].style.transform = `translate3d(${x}px,${y}px,0) scale(${k})`; lb.classList.toggle('zoomed', k > 1.01); };
      const limits = k => ({ mx: Math.max(0, (boxes[vi].w * k - lw) / 2), my: Math.max(0, (boxes[vi].h * k - lh) / 2) });
      const clampZ = (k, x, y) => { const { mx, my } = limits(k); return [clamp(x, -mx, mx), clamp(y, -my, my)]; };
      const rubber = (v, lo, hi) => v < lo ? lo - (lo - v) * .35 : v > hi ? hi + (v - hi) * .35 : v;
      const rel = e => ({ x: e.clientX - lw / 2, y: e.clientY - lh / 2 });
      const loadBig = i => {
        const img = $('img', lbPZ[i]); if (img.dataset.big) return; img.dataset.big = '1';
        const v = VS[i], im = new Image(); im.src = v.z;
        (im.decode ? im.decode() : Promise.resolve()).then(() => { img.src = v.z; }, () => {});
      };
      const zoomTo = (k, fx, fy, dur = 420) => {
        k = clamp(k, 1, MAXZ);
        let tx = fx - (fx - zx) * (k / zs), ty = fy - (fy - zy) * (k / zs);
        [tx, ty] = k === 1 ? [0, 0] : clampZ(k, tx, ty);
        const s0 = zs, x0 = zx, y0 = zy;
        vTween(dur, t => setZ(s0 + (k - s0) * t, x0 + (tx - x0) * t, y0 + (ty - y0) * t));
        if (k > 1.2) loadBig(vi);
      };
      const zoomMouse = e => {
        const k = 2.4, mx = Math.max(0, (boxes[vi].w * k - lw) / 2), my = Math.max(0, (boxes[vi].h * k - lh) / 2);
        const tx = -((e.clientX / lw) * 2 - 1) * mx, ty = -((e.clientY / lh) * 2 - 1) * my, s0 = zs, x0 = zx, y0 = zy;
        vTween(460, t => setZ(s0 + (k - s0) * t, x0 + (tx - x0) * t, y0 + (ty - y0) * t));
        loadBig(vi);
      };
      const settleZ = () => {
        if (zs < 1) return zoomTo(1, 0, 0, 320);
        if (zs > MAXZ) return zoomTo(MAXZ, 0, 0, 320);
        const [tx, ty] = clampZ(zs, zx, zy); if (tx === zx && ty === zy) return;
        const x0 = zx, y0 = zy; vTween(360, t => setZ(zs, x0 + (tx - x0) * t, y0 + (ty - y0) * t));
      };
      const syncV = () => {
        lbCount.textContent = `${pad(vi + 1)} / ${pad(VS.length)}`;
        lbTh.forEach((b, k) => { b.classList.toggle('on', k === vi); b.setAttribute('aria-label', `Image ${k + 1}`); });
        lbS.forEach((sl, k) => sl.classList.toggle('cur', k === vi));
        lbPrev.disabled = vi === 0; lbNext.disabled = vi === VS.length - 1;
      };
      const vGo = (i, dur = 460) => {
        i = clamp(i, 0, VS.length - 1);
        if (i !== vi) { lbPZ[vi].style.transform = ''; zs = 1; zx = zy = 0; lb.classList.remove('zoomed'); }
        const from = trackX, to = -i * lw; vi = i; syncV();
        vTween(dur, t => setTrack(from + (to - from) * t));
      };
      const flyFrom = i => { const r = VS[i].el.getBoundingClientRect(), b = boxes[i], k = Math.min(r.width / b.w, r.height / b.h) || .5; return { x: r.left + r.width / 2 - lw / 2, y: r.top + r.height / 2 - lh / 2, k }; };
      const showHint = () => {
        lbHint.textContent = fineVP.matches ? lbHint.dataset.fine : lbHint.dataset.touch;
        lbHint.classList.add('show'); clearTimeout(hintT); hintT = setTimeout(() => lbHint.classList.remove('show'), 2600);
      };
      const openLb = (i = index) => {
        if (vOpen) return; vOpen = true; vi = i;
        lb.classList.add('on', 'chrome-off'); lb.classList.toggle('zoomable', fineVP.matches); lb.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        vLayout(); syncV();
        const f = flyFrom(vi), pz = lbPZ[vi], cut = VS[vi].cut, src = VS[vi].el;
        src.style.visibility = 'hidden';
        vTween(520, t => {
          pz.style.transform = `translate3d(${f.x * (1 - t)}px,${f.y * (1 - t)}px,0) scale(${f.k + (1 - f.k) * t})`;
          if (!cut) pz.style.opacity = Math.min(1, t * 2.4);
          lbBg.style.opacity = t;
        }, () => { pz.style.transform = ''; pz.style.opacity = ''; src.style.visibility = ''; lb.classList.remove('chrome-off'); showHint(); });
        lbX.focus({ preventScroll: true });
      };
      const closeLb = () => {
        if (!vOpen) return; vOpen = false;
        sc.classList.add('free'); sc.scrollLeft = slides[vi].offsetLeft; sc.classList.remove('free'); onGalScroll();
        const i = vi, pz = lbPZ[i], f = flyFrom(i), s0 = zs * (1 - Math.min(1, dismissY / (lh * .6)) * .25), x0 = zx, y0 = zy + dismissY, cut = VS[i].cut, bg0 = lbBg.style.opacity === '' ? 1 : +lbBg.style.opacity, src = VS[i].el;
        src.style.visibility = 'hidden'; lb.classList.add('chrome-off'); lbHint.classList.remove('show');
        vTween(440, t => {
          pz.style.transform = `translate3d(${x0 + (f.x - x0) * t}px,${y0 + (f.y - y0) * t}px,0) scale(${s0 + (f.k - s0) * t})`;
          if (!cut) pz.style.opacity = 1 - Math.max(0, t - .5) / .5;
          lbBg.style.opacity = bg0 * (1 - t);
        }, () => {
          lb.classList.remove('on', 'zoomed', 'chrome-off'); lb.setAttribute('aria-hidden', 'true'); document.documentElement.style.overflow = '';
          pz.style.transform = ''; pz.style.opacity = ''; zs = 1; zx = zy = 0; dismissY = 0; src.style.visibility = '';
          sc.focus({ preventScroll: true });
        });
      };
      this.closeLb = closeLb;
      this._off.push(() => { if (vOpen) document.documentElement.style.overflow = ''; });
      const ptrs = new Map(); let G = null, lastTap = null, tapT = 0, hover = null, hoverRaf = 0, wheelAcc = 0, wheelLock = 0;
      this.on(lbTrack, 'pointerdown', e => {
        if (!vOpen) return;
        cancelAnimationFrame(vAnim); vAnim = 0;
        try { lbTrack.setPointerCapture(e.pointerId); } catch {}
        ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const now = performance.now();
        if (ptrs.size === 1) G = { k: 'wait', sx: e.clientX, sy: e.clientY, x0: zx, y0: zy, tr0: trackX, vx: 0, vy: 0, lx: e.clientX, ly: e.clientY, lt: now, mouse: e.pointerType === 'mouse' };
        else if (ptrs.size === 2) {
          const [p1, p2] = [...ptrs.values()];
          setTrack(-vi * lw);
          G = { k: 'pinch', d0: Math.hypot(p1.x - p2.x, p1.y - p2.y), s0: zs, x0: zx, y0: zy, c0: { x: (p1.x + p2.x) / 2 - lw / 2, y: (p1.y + p2.y) / 2 - lh / 2 } };
        }
      });
      const hoverPan = e => {
        const { mx, my } = limits(zs);
        hover = { x: -((e.clientX / lw) * 2 - 1) * mx, y: -((e.clientY / lh) * 2 - 1) * my };
        if (hoverRaf) return;
        const f = () => {
          if (!hover || zs <= 1.01 || vAnim) { hoverRaf = 0; return; }
          const nx = zx + (hover.x - zx) * .14, ny = zy + (hover.y - zy) * .14;
          setZ(zs, nx, ny);
          hoverRaf = Math.abs(hover.x - nx) + Math.abs(hover.y - ny) > .3 ? requestAnimationFrame(f) : 0;
        };
        hoverRaf = requestAnimationFrame(f);
      };
      this.on(lbTrack, 'pointermove', e => {
        if (!vOpen) return;
        if (!ptrs.has(e.pointerId)) { if (e.pointerType === 'mouse' && zs > 1.01) hoverPan(e); return; }
        ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (!G) return;
        if (G.k === 'pinch') {
          if (ptrs.size < 2) return;
          const [p1, p2] = [...ptrs.values()], d = Math.hypot(p1.x - p2.x, p1.y - p2.y), c = { x: (p1.x + p2.x) / 2 - lw / 2, y: (p1.y + p2.y) / 2 - lh / 2 };
          const k = clamp(G.s0 * d / G.d0, .7, MAXZ * 1.3);
          setZ(k, c.x - (G.c0.x - G.x0) * (k / G.s0), c.y - (G.c0.y - G.y0) * (k / G.s0));
          if (k > 1.2) loadBig(vi);
          return;
        }
        const dx = e.clientX - G.sx, dy = e.clientY - G.sy, now = performance.now(), dt = Math.max(1, now - G.lt);
        G.vx = G.vx * .6 + (e.clientX - G.lx) / dt * .4; G.vy = G.vy * .6 + (e.clientY - G.ly) / dt * .4; G.lx = e.clientX; G.ly = e.clientY; G.lt = now;
        if (G.k === 'wait') {
          if (Math.hypot(dx, dy) < 7) return;
          G.k = zs > 1.01 ? (G.mouse ? 'none' : 'pan') : Math.abs(dx) > Math.abs(dy) ? 'swipe' : dy > 0 && !G.mouse ? 'dismiss' : 'none';
          if (G.k === 'dismiss') { lb.classList.add('chrome-off'); lbHint.classList.remove('show'); }
        }
        if (G.k === 'pan') { const { mx, my } = limits(zs); setZ(zs, rubber(G.x0 + dx, -mx, mx), rubber(G.y0 + dy, -my, my)); }
        else if (G.k === 'swipe') setTrack(rubber(G.tr0 + dx, -(VS.length - 1) * lw, 0));
        else if (G.k === 'dismiss') {
          dismissY = Math.max(0, dy);
          const k = Math.min(1, dismissY / (lh * .6));
          lbPZ[vi].style.transform = `translate3d(0,${dismissY}px,0) scale(${1 - k * .25})`;
          lbBg.style.opacity = 1 - k;
        }
      });
      const fling = (vx, vy) => {
        let x = zx, y = zy; const { mx, my } = limits(zs);
        if (x < -mx || x > mx || y < -my || y > my || Math.abs(vx) + Math.abs(vy) < .08) return settleZ();
        let last = performance.now();
        const f = now => {
          const dt = Math.min(32, now - last); last = now;
          const decay = Math.pow(.994, dt); vx *= decay; vy *= decay; x += vx * dt; y += vy * dt;
          const cx = clamp(x, -mx, mx), cy = clamp(y, -my, my);
          if (cx !== x) { x = cx; vx = 0; } if (cy !== y) { y = cy; vy = 0; }
          setZ(zs, x, y);
          vAnim = Math.abs(vx) + Math.abs(vy) > .02 ? requestAnimationFrame(f) : 0;
        };
        vAnim = requestAnimationFrame(f);
      };
      const vTap = (e, g) => {
        if (g.mouse) return zs > 1.01 ? zoomTo(1, 0, 0) : zoomMouse(e);
        const pt = rel(e), now = performance.now();
        if (lastTap && now - lastTap.t < 300 && Math.hypot(pt.x - lastTap.x, pt.y - lastTap.y) < 40) {
          clearTimeout(tapT); lastTap = null;
          return zs > 1.01 ? zoomTo(1, 0, 0) : zoomTo(2.4, pt.x, pt.y);
        }
        lastTap = { t: now, x: pt.x, y: pt.y };
        tapT = setTimeout(() => { lastTap = null; if (zs <= 1.01) lb.classList.toggle('chrome-off'); }, 300);
      };
      const endPtr = e => {
        if (!ptrs.has(e.pointerId)) return;
        ptrs.delete(e.pointerId);
        if (!G) return;
        if (G.k === 'pinch') {
          if (ptrs.size === 1) { const p1 = [...ptrs.values()][0]; G = { k: 'pan', sx: p1.x, sy: p1.y, x0: zx, y0: zy, vx: 0, vy: 0, lx: p1.x, ly: p1.y, lt: performance.now() }; return; }
          G = null; return settleZ();
        }
        const g = G; G = null;
        if (e.type === 'pointercancel') { settleZ(); vGo(vi, 300); return; }
        if (g.k === 'wait') return vTap(e, g);
        if (g.k === 'pan') return fling(g.vx, g.vy);
        if (g.k === 'swipe') { const dx = e.clientX - g.sx, dir = Math.abs(dx) > lw * .18 || Math.abs(g.vx) > .45 ? (dx < 0 ? 1 : -1) : 0; return vGo(vi + dir, 420); }
        if (g.k === 'dismiss') {
          if (dismissY > lh * .16 || g.vy > .6) return closeLb();
          const y0 = dismissY, pz = lbPZ[vi], k0 = Math.min(1, y0 / (lh * .6));
          vTween(320, t => { pz.style.transform = `translate3d(0,${y0 * (1 - t)}px,0) scale(${1 - k0 * (1 - t) * .25})`; lbBg.style.opacity = 1 - k0 * (1 - t); }, () => { pz.style.transform = ''; dismissY = 0; lb.classList.remove('chrome-off'); });
        }
      };
      this.on(lbTrack, 'pointerup', endPtr); this.on(lbTrack, 'pointercancel', endPtr);
      this.on(lb, 'wheel', e => {
        if (!vOpen) return;
        e.preventDefault();
        if (e.ctrlKey) {
          cancelAnimationFrame(vAnim); vAnim = 0;
          const pt = rel(e), k = clamp(zs * Math.exp(-e.deltaY * .012), 1, MAXZ);
          let tx = pt.x - (pt.x - zx) * (k / zs), ty = pt.y - (pt.y - zy) * (k / zs);
          [tx, ty] = k <= 1 ? [0, 0] : clampZ(k, tx, ty);
          setZ(k, tx, ty); if (k > 1.2) loadBig(vi);
          return;
        }
        if (zs > 1.01) { const [tx, ty] = clampZ(zs, zx - e.deltaX, zy - e.deltaY); setZ(zs, tx, ty); return; }
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || performance.now() < wheelLock) return;
        wheelAcc += e.deltaX;
        if (Math.abs(wheelAcc) > 50) { vGo(vi + Math.sign(wheelAcc)); wheelAcc = 0; wheelLock = performance.now() + 500; }
      }, { passive: false });
      this.on(lb, 'keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); if (zs <= 1.01) vGo(vi + (e.key === 'ArrowRight' ? 1 : -1)); }
        else if (e.key === '+' || e.key === '=') zoomTo(zs * 1.5, 0, 0);
        else if (e.key === '-') zoomTo(zs / 1.5, 0, 0);
      });
      q('zoom').onclick = e => { e.stopPropagation(); openLb(); };
      lbX.onclick = closeLb;
      lbPrev.onclick = () => vGo(vi - 1); lbNext.onclick = () => vGo(vi + 1);
      allLbTh.forEach(b => b.onclick = () => { const k = lbTh.indexOf(b); if (k >= 0) vGo(k); });
      this.on(window, 'resize', () => { if (!vOpen) return; lbPZ[vi].style.transform = ''; zs = 1; zx = zy = 0; lb.classList.remove('zoomed'); vLayout(); });

      const colourKey = value => (value || '').trim().toLowerCase();
      let swapGhost = null, swapFade = null;
      const clearSwap = () => { swapGhost?.remove(); swapGhost = null; swapFade?.cancel(); swapFade = null; };
      const filterColour = value => {
        const key = colourKey(value);
        let chosen = allSlides.filter(s => !colourKey(s.dataset.colour) || colourKey(s.dataset.colour) === key);
        if (!chosen.length) chosen = allSlides;
        if (chosen.length === slides.length && chosen.every((s, i) => s === slides[i])) return;
        clearSwap();
        if (!reduce() && slides[index] && sc.offsetParent) {
          swapGhost = slides[index].cloneNode(true);
          swapGhost.setAttribute('aria-hidden', 'true');
          swapGhost.style.cssText = 'position:absolute;inset:0;z-index:2;pointer-events:none';
          swapGhost.firstElementChild.style.cssText = 'animation:none;transform:none;opacity:1';
          swapGhost.lastElementChild.style.cssText = 'animation:none;opacity:1';
          sc.parentElement.appendChild(swapGhost);
          const ghost = swapGhost;
          ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: 'ease-out' }).onfinish = () => { ghost.remove(); if (swapGhost === ghost) swapGhost = null; };
          swapFade = sc.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
        }
        cancelAnimationFrame(anim); cancelAnimationFrame(sraf); cancelAnimationFrame(vAnim);
        anim = sraf = vAnim = 0; drag = null; G = null; ptrs.clear();
        countNow.getAnimations().forEach(a => { a.onfinish = null; a.cancel(); });
        allSlides.forEach((s, i) => {
          const hidden = !chosen.includes(s);
          s.hidden = allThumbs[i].hidden = allLbS[i].hidden = allLbTh[i].hidden = hidden;
          allThumbs[i].classList.remove('on'); allThumbs[i].setAttribute('aria-selected', 'false'); allThumbs[i].tabIndex = -1;
          allLbS[i].classList.remove('cur'); allLbTh[i].classList.remove('on');
          allVS[i].el.style.visibility = ''; allLbPZ[i].style.transform = ''; allLbPZ[i].style.opacity = '';
        });
        slides = chosen; thumbs = allThumbs.filter(t => !t.hidden); N = slides.length;
        const firstImage = $('img', slides[0]);
        flightImages.forEach(img => { img.src = firstImage.getAttribute('src'); });
        VS = allVS.filter((v, i) => !allSlides[i].hidden); lbS = allLbS.filter(s => !s.hidden); lbPZ = allLbPZ.filter((p, i) => !allSlides[i].hidden); lbTh = allLbTh.filter(t => !t.hidden);
        index = -1; vi = 0; zs = 1; zx = zy = dismissY = 0; hover = null;
        lb.classList.remove('zoomed');
        galLine.style.width = 100 / N + '%'; q('countall').textContent = '/ ' + pad(N);
        sc.classList.add('free'); sc.classList.remove('grabbing'); sc.scrollLeft = 0; sc.classList.remove('free');
        setIndex(0); countNow.getAnimations().forEach(a => { a.onfinish = null; a.cancel(); }); countNow.textContent = '01';
        onGalScroll(); vLayout(); syncV();
      };
      // A colour's pictures are hidden until chosen, so they never loaded and the first swap
      // cross-faded into an empty frame. They load on intent (hover, focus, touch on a swatch;
      // each colour's first picture when the page is idle), and the swap waits briefly for
      // the first one to decode.
      const colourImages = value => {
        const key = colourKey(value);
        return allSlides.flatMap((s, i) => colourKey(s.dataset.colour) === key ? [$('img', s), $('img', allThumbs[i])] : []).filter(Boolean);
      };
      const warmColour = value => colourImages(value).forEach(img => { if (img.loading === 'lazy') img.loading = 'eager'; });
      let swapToken = 0;
      const showColour = value => {
        const token = ++swapToken, first = colourImages(value)[0];
        if (!first || (first.complete && first.naturalWidth) || reduce()) return filterColour(value);
        warmColour(value);
        Promise.race([first.decode().catch(() => {}), new Promise(r => setTimeout(r, 450))])
          .then(() => { if (token === swapToken && this.isConnected) filterColour(value); });
      };
      $$('.mc-swatch[data-value]', root).forEach(b => {
        const warm = () => warmColour(b.dataset.value);
        this.on(b, 'pointerenter', warm); this.on(b, 'focus', warm); this.on(b, 'touchstart', warm, { passive: true });
      });
      if (!navigator.connection?.saveData) {
        const idle = window.requestIdleCallback || (fn => setTimeout(fn, 1200));
        idle(() => $$('.mc-swatch[data-value]', root).forEach(b => { const f = colourImages(b.dataset.value)[0]; if (f && f.loading === 'lazy') f.loading = 'eager'; }));
      }
      this._off.push(() => { clearSwap(); swapToken++; cancelAnimationFrame(anim); cancelAnimationFrame(sraf); cancelAnimationFrame(vAnim); cancelAnimationFrame(hoverRaf); clearTimeout(hintT); clearTimeout(tapT); });

      // the purchase panel is absent for unreleased products (the store's coming-soon form shows instead)
      if (!q('atc')) { this.pauseOff(q('gal'), [$('svg', galRing)]); return; }

      /* ---------- choosing: colour, size ---------- */
      const variants = data.variants || [], money = v => v && v.price_formatted;
      const sizeBtns = $$('.mc-size', root), pill = q('pill'), sizeMsg = q('sizemsg'), avail = q('avail'), status = q('status');
      const colourBtns = $$('.mc-swatch[data-value]', root), atc = q('atc'), err = q('err');
      let size = null, colour = data.colour || null;
      const needsSize = sizeBtns.length > 0;
      const setAvail = txt => { if (avail) avail.innerHTML = `<span>${txt}</span>`; };
      const current = () => variants.find(v => (data.colourIndex == null || v.options[data.colourIndex] === colour) && (data.sizeIndex == null || !size || v.options[data.sizeIndex] === size)) || variants.find(v => data.colourIndex == null || v.options[data.colourIndex] === colour) || variants[0];
      const syncPrice = () => {
        const v = current(); if (!v) return;
        $$('[data-mc-price-text]', document).forEach(el => el.textContent = v.price_formatted);
        const ok = v.available;
        atc.disabled = !ok; atc.querySelector('.mc-atc__lbl span').textContent = ok ? data.labels.add : data.labels.soldOut;
        const mb = $('[data-mc-mbar-btn]', this.mbar || document); if (mb) mb.disabled = !ok;
      };
      const pickColour = b => {
        colour = b.dataset.value;
        showColour(colour);
        colourBtns.forEach(s => { s.setAttribute('aria-checked', s === b); s.tabIndex = s === b ? 0 : -1; });
        const em = q('colourname'); if (em) em.textContent = colour;
        const note = q('colournote'); if (note) note.textContent = allSlides.some(s => colourKey(s.dataset.colour) === colourKey(colour) && colourKey(colour)) ? '' : b.dataset.note || '';
        // sizes that are real variants follow the colour's availability
        if (data.sizeIndex != null) sizeBtns.forEach(s => { const v = variants.find(x => x.options[data.colourIndex] === colour && x.options[data.sizeIndex] === s.dataset.value); s.setAttribute('aria-disabled', !(v && v.available)); });
        syncPrice(); syncMeta();
      };
      colourBtns.forEach(b => b.onclick = () => pickColour(b));
      const syncMeta = () => {
        const txt = [colour, size].filter(Boolean).join(' · ');
        $$('[data-mc-meta]', document).forEach(el => el.textContent = txt);
      };
      const pickSize = b => {
        if (!b || b.getAttribute('aria-disabled') === 'true') return;
        size = b.dataset.value;
        sizeBtns.forEach(s => { s.setAttribute('aria-checked', s === b); s.tabIndex = s === b ? 0 : -1; });
        pill.style.width = b.offsetWidth + 'px'; pill.style.transform = `translateX(${b.offsetLeft}px)`; pill.style.opacity = 1;
        sizeMsg.classList.remove('on'); status && status.classList.add('mc-picked');
        setAvail((data.labels.sizeAvailable || 'Size [size] is available').replace('[size]', size).replace('[colour]', colour || ''));
        const mb = $('[data-mc-mbar-btn]', this.mbar || document); if (mb) mb.textContent = (data.labels.addSize || 'Add [size] to bag').replace('[size]', size);
        syncPrice(); syncMeta();
      };
      sizeBtns.forEach(b => b.onclick = () => b.getAttribute('aria-disabled') === 'true' ? setAvail((data.labels.soldOutSize || '[size] has sold out').replace('[size]', b.dataset.value)) : pickSize(b));
      const sizesBox = q('sizes');
      if (sizesBox) {
        this.on(sizesBox, 'keydown', e => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const live = sizeBtns.filter(b => b.getAttribute('aria-disabled') !== 'true');
          const i = live.findIndex(b => b.dataset.value === size), n = live[(i + (e.key === 'ArrowRight' ? 1 : -1) + live.length) % live.length];
          pickSize(n); n.focus();
        });
        this.observe(ResizeObserver, () => { const b = sizeBtns.find(s => s.dataset.value === size); if (b) { pill.style.transition = 'none'; pill.style.width = b.offsetWidth + 'px'; pill.style.transform = `translateX(${b.offsetLeft}px)`; requestAnimationFrame(() => pill.style.transition = ''); } }, sizesBox);
      }
      const firstColour = colourBtns.find(b => b.getAttribute('aria-checked') === 'true') || colourBtns[0];
      if (firstColour) pickColour(firstColour); else syncPrice();

      /* ---------- the phone buy bar, the size sheet, the size guide, the "added" card ---------- */
      const mbar = this.mbar = this.portal(q('mbar')), sheet = this.portal(q('sheet')), sg = this.portal(q('sg')), mini = this.portal(q('mini')), scrim = this.portal(q('scrim'));
      const inView = el => { if (!el) return false; const r = el.getBoundingClientRect(); return r.bottom > 40 && r.top < innerHeight - 40 && r.width > 0; };
      let openD = null;
      const openSheet = () => { sheet.classList.add('on'); sheet.setAttribute('aria-hidden', 'false'); scrim.classList.add('on'); $('.mc-sheet__row', sheet)?.focus({ preventScroll: true }); };
      const closeSheet = () => { sheet.classList.remove('on'); sheet.setAttribute('aria-hidden', 'true'); if (!openD) scrim.classList.remove('on'); };
      const openDrawer = d => { closeSheet(); openD = d; d.classList.add('on'); d.setAttribute('aria-hidden', 'false'); scrim.classList.add('on'); document.documentElement.style.overflow = 'hidden'; $('.mc-x', d)?.focus(); };
      const closeDrawer = () => { if (!openD) return; openD.classList.remove('on'); openD.setAttribute('aria-hidden', 'true'); scrim.classList.remove('on'); document.documentElement.style.overflow = ''; openD = null; };
      $$('.mc-sheet__row', sheet).forEach(r => r.onclick = () => { if (r.getAttribute('aria-disabled') === 'true') return; pickSize(sizeBtns.find(b => b.dataset.value === r.dataset.size)); closeSheet(); setTimeout(() => addToBag('bar'), reduce() ? 0 : 250); });
      $$('[data-mc-sgopen]', document).forEach(a => { if (a.closest('mc-piece') !== root && !sheet.contains(a)) return; a.onclick = e => { if (!sg.querySelector('.mc-sg-body')?.childElementCount && !sg.querySelector('.mc-sg-body')?.textContent.trim()) return; e.preventDefault(); openDrawer(sg); }; });
      scrim.onclick = () => { closeDrawer(); closeSheet(); };
      $$('[data-close]', sg).forEach(b => b.onclick = closeDrawer);
      this.on(window, 'keydown', e => { if (e.key !== 'Escape') return; if (lb.classList.contains('on')) return closeLb(); closeDrawer(); closeSheet(); hideMini(); });
      this._off.push(() => { if (openD) document.documentElement.style.overflow = ''; });

      let atcAbove = false;
      const syncBar = () => { const show = atcAbove && !mini.classList.contains('on'); mbar.classList.toggle('on', show); mbar.setAttribute('aria-hidden', !show); const b = $('[data-mc-mbar-btn]', mbar); if (b) b.tabIndex = show ? 0 : -1; };
      this.observe(IntersectionObserver, ([e]) => { atcAbove = !e.isIntersecting && e.boundingClientRect.top < 0 && root.getBoundingClientRect().bottom > 0; syncBar(); }, atc);
      this.observe(IntersectionObserver, ([e]) => { if (!e.isIntersecting) { atcAbove = false; syncBar(); } }, root);

      let miniTimer = 0;
      const hideMini = () => { mini.classList.remove('on'); mini.setAttribute('aria-hidden', 'true'); clearTimeout(miniTimer); syncBar(); };
      const hideMiniIfIdle = () => { if (mini.matches(':hover,:focus-within')) { miniTimer = setTimeout(hideMiniIfIdle, 1200); return; } hideMini(); };
      const showMini = count => {
        $$('[data-mc-meta]', mini).forEach(el => el.textContent = [colour, size].filter(Boolean).join(' · '));
        const view = $('[data-mc-view]', mini); if (view) view.textContent = (data.labels.viewBag || 'View bag') + (count ? ` (${count})` : '');
        const bar = $('.mc-mini__bar i', mini); bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = '';
        mini.classList.add('on'); mini.setAttribute('aria-hidden', 'false');
        mbar.classList.remove('on');
        clearTimeout(miniTimer); miniTimer = setTimeout(hideMiniIfIdle, 5000);
      };
      this._off.push(() => clearTimeout(miniTimer));
      $('.mc-x', mini).onclick = hideMini;
      const cartDrawer = () => document.querySelector('cart-drawer');
      $('[data-mc-view]', mini).onclick = () => { hideMini(); const cd = cartDrawer(); if (cd && typeof cd.open === 'function') cd.open(); else location.href = (window.routes?.cart_url || '/cart'); };
      let sy0 = null;
      this.on(mini, 'touchstart', e => sy0 = e.touches[0].clientY, { passive: true });
      this.on(mini, 'touchend', e => { if (sy0 !== null && e.changedTouches[0].clientY - sy0 > 40) hideMini(); sy0 = null; });

      /* ---------- add to bag: stitch, turn, fly, catch ---------- */
      let ringAngle = 0, busy = false;
      const bagTarget = () => $('#cart-counter .icon, #cart-counter svg, #cart-counter') || $('a[href$="/cart"]');
      const needSize = fromBar => {
        if (fromBar || (innerWidth < 700 && !inView(sizesBox))) return openSheet();
        sizesBox.classList.remove('mc-shake'); void sizesBox.offsetWidth; sizesBox.classList.add('mc-shake');
        sizeMsg.classList.add('on');
        if (!inView(sizesBox)) sizesBox.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'center' });
      };
      const fly = src => {
        const cutout = $('img', slides[0]);
        const target = bagTarget(); if (!target) return Promise.resolve();
        const a = src.getBoundingClientRect(), b = target.getBoundingClientRect();
        const w = Math.min(a.width, 260), h = w * (a.height / a.width);
        const sx = a.left + a.width / 2, sy = a.top + a.height / 2, ex = b.left + b.width / 2, ey = b.top + b.height / 2;
        const outer = document.createElement('div'), inner = document.createElement('div'), img = new Image();
        outer.className = 'fly-x'; inner.className = 'fly-y'; img.src = cutout.currentSrc || cutout.src; img.alt = ''; img.style.width = w + 'px'; img.style.height = h + 'px';
        inner.appendChild(img); outer.appendChild(inner); document.body.appendChild(outer);
        outer.style.left = sx - w / 2 + 'px'; outer.style.top = sy - h / 2 + 'px';
        const dur = 900, s = 22 / w;
        outer.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${ex - sx}px)` }], { duration: dur, easing: 'cubic-bezier(.55,0,.8,.45)', fill: 'forwards' });
        inner.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${ey - sy}px)` }], { duration: dur, easing: 'cubic-bezier(.2,.75,.35,1)', fill: 'forwards' });
        const an = img.animate([
          { transform: 'scale(1) rotate(0)', opacity: .95 },
          { transform: `scale(${Math.max(s, .55)}) rotate(-6deg)`, opacity: 1, offset: .45 },
          { transform: `scale(${s}) rotate(4deg)`, opacity: .9, offset: .92 },
          { transform: `scale(${s * .6}) rotate(4deg)`, opacity: 0 }
        ], { duration: dur, easing: 'cubic-bezier(.45,0,.55,1)', fill: 'forwards' });
        return new Promise(res => { let done = false; const end = () => { if (done) return; done = true; outer.remove(); res(); }; an.onfinish = end; setTimeout(end, dur + 250); });
      };
      const addToBag = async origin => {
        if (busy) return;
        if (needsSize && !size) return needSize(origin === 'bar');
        const v = current();
        if (!v || !v.available) { err.textContent = data.labels.soldOut; return; }
        busy = true; err.textContent = '';
        const fd = new FormData();
        fd.append('id', v.id); fd.append('quantity', '1');
        if (needsSize && data.sizeIndex == null) fd.append(`properties[${data.sizeProperty || 'Size'}]`, size);
        const cd = cartDrawer();
        let sections = [];
        try { sections = cd && cd.getSectionsToRender ? cd.getSectionsToRender().map(s => s.section) : []; } catch {}
        if (sections.length) { fd.append('sections', sections); fd.append('sections_url', location.pathname); }
        const request = fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd }).then(r => r.json());
        atc.classList.add('busy');
        const waitT = setTimeout(() => atc.classList.add('wait'), 650);   // a slow network: the stitch keeps sewing
        if (!reduce()) {
          galRing.getAnimations().forEach(a => a.cancel());
          galRing.animate([{ transform: `rotate(${ringAngle}deg) scale(1)` }, { transform: `rotate(${ringAngle + 90}deg) scale(1.05)`, offset: .35 }, { transform: `rotate(${ringAngle + 180}deg) scale(1)` }], { duration: 1600, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' });
          ringAngle += 180;
          if (index === 0 && inView(sc)) $('img', slides[0]).animate([{ transform: 'none' }, { transform: 'translateY(-14px) scale(.965)', offset: .3 }, { transform: 'none' }], { duration: 900, easing: 'cubic-bezier(.33,1,.68,1)' });
        }
        let res;
        try { res = await request; } catch (e) { res = { status: 500, description: data.labels.error }; }
        clearTimeout(waitT); atc.classList.remove('wait');
        if (res.status && res.status >= 400 || res.errors) {
          atc.classList.remove('busy'); busy = false;
          err.textContent = res.description || res.message || data.labels.error;
          return;
        }
        const src = inView(sc) ? $('img', slides[index]) : (inView($('img', mbar)) ? $('img', mbar) : null);
        const flight = src && !reduce() ? fly(src) : Promise.resolve();
        await wait(reduce() ? 0 : 560);
        atc.classList.remove('busy'); atc.classList.add('done');
        await flight;
        try { if (cd && res.sections && cd.renderContents) cd.renderContents(res, false); } catch {}
        try { window.updateCartCounters && window.updateCartCounters(); } catch {}
        let count = 0;
        try { count = (await (await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } })).json()).item_count; } catch {}
        const t = bagTarget();
        if (t && !reduce()) t.animate([{ transform: 'none' }, { transform: 'scale(1.16,.84)', offset: .25 }, { transform: 'scale(.93,1.08)', offset: .55 }, { transform: 'none' }], { duration: 560, easing: 'ease-out' });
        showMini(count);
        await wait(1300);
        atc.classList.remove('done');
        busy = false;
      };
      atc.onclick = () => addToBag('main');
      const mb = $('[data-mc-mbar-btn]', mbar); if (mb) mb.onclick = () => addToBag('bar');
      this.on(document, 'mc:choose-size', () => { scrollTo({ top: 0, behavior: reduce() ? 'auto' : 'smooth' }); setTimeout(() => (size ? atc : sizeBtns[0] || atc)?.focus({ preventScroll: true }), reduce() ? 0 : 900); });

      /* ---------- accordion ---------- */
      $$('.mc-acc__btn', root).forEach(b => b.onclick = () => { const it = b.parentElement, on = !it.classList.contains('open'); it.classList.toggle('open', on); b.setAttribute('aria-expanded', on); });

      /* ---------- continuous animations only while on screen; the entrance ---------- */
      this.pauseOff(q('gal'), [$('svg', galRing)]);
      this.pauseOff(status || root, [$('.mc-seal__ring', root)]);
      $$('.mc-leaf', root).forEach(l => this.pauseOff(l, [l]));
      if (!reduceNow) {
        galRing.animate([{ transform: 'rotate(-50deg) scale(.86)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 1600, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    }
  }

  /* ================= card 2: the details ================= */
  class Details extends Card {
    setup() {
      const root = this, focus = root.dataset.style === 'focus', grid = $('.mc-det', root) || root;
      const hots = $$('.mc-hot', root), items = $$('.mc-det__item', root), lenses = $$('.mc-lens__img', root), lens = $('.mc-lens', root);
      const n = hots.length; if (!n) return;
      const q = s => $(`[data-mc-${s}]`, root);
      const lensN = q('lensn'), num = q('detnum');
      const DET = items.map(it => ({ t: $('.mc-t', it)?.textContent || '', d: $('em', it)?.textContent || '' }));
      const oneFrame = matchMedia('(max-width:1099px)');
      let dIdx = 0, autoOn = !focus, autoTimer = 0, visible = false;
      const aim = () => {
        if (!lens || !hots[dIdx]) return;
        const h = hots[dIdx].getBoundingClientRect(), r = lens.getBoundingClientRect();
        if (!r.width) return;
        lens.style.setProperty('--ox', ((h.left + h.width / 2 - r.left) / r.width * 100).toFixed(1) + '%');
        lens.style.setProperty('--oy', ((h.top + h.height / 2 - r.top) / r.height * 100).toFixed(1) + '%');
      };
      const show = (i, user) => {
        if (user && autoOn) { autoOn = false; grid.classList.remove('auto'); clearTimeout(autoTimer); }
        const open = grid.dataset.state === 'detail';
        if (i !== dIdx) {
          const prev = lenses[dIdx], next = lenses[i];
          lenses.forEach(l => l.classList.remove('prev'));
          if (prev) { prev.classList.remove('on'); prev.classList.add('prev'); }
          if (next) {
            next.classList.add('on');
            if (!reduce() && (!focus || open || !oneFrame.matches)) next.animate([{ clipPath: 'inset(100% 0 0 0)', transform: 'scale(1.08)' }, { clipPath: 'inset(0 0 0 0)', transform: 'none' }], { duration: 880, easing: 'cubic-bezier(.76,0,.24,1)' });
          }
          if (num) {
            if (reduce() || !num.offsetParent) num.textContent = pad(i + 1);
            else num.animate([{ transform: 'none' }, { transform: 'translateY(-100%)' }], { duration: 260, easing: 'cubic-bezier(.5,0,.75,0)' }).onfinish = () => {
              num.textContent = pad(i + 1);
              num.animate([{ transform: 'translateY(100%)' }, { transform: 'none' }], { duration: 700, easing: 'cubic-bezier(.16,1,.3,1)' });
            };
          }
          dIdx = i;
        }
        hots.forEach((h, k) => h.classList.toggle('on', k === i));
        items.forEach((it, k) => { it.classList.toggle('on', k === i); it.setAttribute('aria-selected', k === i); });
        if (lensN) lensN.textContent = `${pad(i + 1)} / ${pad(n)}`;
        $$('[data-mc-desc]', root).forEach(el => el.textContent = DET[i].d);
        const cn = q('capn'), ct = q('capt'); if (cn) cn.textContent = pad(i + 1); if (ct) ct.textContent = DET[i].t;
        if (focus && oneFrame.matches) { aim(); grid.dataset.state = 'detail'; }
        if (autoOn) queueAuto();
      };
      const close = () => {
        if (grid.dataset.state !== 'detail') return;
        aim(); grid.dataset.state = 'overview';
        hots.forEach(h => h.classList.remove('on'));
        items.forEach(it => { it.classList.remove('on'); it.setAttribute('aria-selected', 'false'); });
      };
      const queueAuto = () => {
        clearTimeout(autoTimer);
        if (!autoOn || !visible || reduce()) { grid.classList.remove('auto'); return; }
        grid.classList.remove('auto'); void grid.offsetWidth; grid.classList.add('auto');
        autoTimer = setTimeout(() => show((dIdx + 1) % n), 4500);
      };
      this._off.push(() => clearTimeout(autoTimer));
      const fineVP = matchMedia('(hover:hover) and (pointer:fine)');
      hots.forEach(h => {
        h.onclick = () => show(+h.dataset.d, true);
        h.onmouseenter = () => { if (fineVP.matches && !(focus && oneFrame.matches)) show(+h.dataset.d, true); };
      });
      items.forEach(it => it.onclick = () => show(+it.dataset.d, true));
      const pv = q('prevd'), nx = q('nextd'), bk = q('back');
      if (pv) pv.onclick = () => show((dIdx + n - 1) % n, true);
      if (nx) nx.onclick = () => show((dIdx + 1) % n, true);
      if (bk) bk.onclick = close;
      if (lens && focus) this.on(lens, 'click', () => { if (oneFrame.matches) close(); });
      const fig = $('.mc-det__fig', root);
      this.observe(IntersectionObserver, ([e]) => { visible = e.isIntersecting && e.intersectionRatio >= .55; queueAuto(); }, fig, { threshold: [0, .55] });
      this.observe(IntersectionObserver, ([e]) => fig.classList.toggle('off', !e.isIntersecting), fig);
      this.pauseOff(root, [$('.mc-det__star', root)]);
      if (focus) {
        const mode = () => { if (oneFrame.matches) close(); else show(dIdx); };
        this.on(oneFrame, 'change', mode); mode();
      } else show(0);
    }
  }

  /* ================= card 3: on figure ================= */
  class Figure extends Card {
    setup() {
      const root = this;
      this.on(root, 'click', e => { if (e.target.closest('[data-mc-buy]')) { e.preventDefault(); document.dispatchEvent(new CustomEvent('mc:choose-size')); } });
      if (root.dataset.style !== 'looks') return;
      const scenes = $$('.mc-scene', root), tabs = $$('.mc-look-tab', root), itemsBox = $('[data-mc-items]', root), lead = $('[data-mc-lead]', root);
      const tpls = $$('template[data-mc-look]', root);
      if (!scenes.length) return;
      let look = -1, t = 0;
      const set = i => {
        i = (i + scenes.length) % scenes.length; if (i === look) return;
        const first = look < 0; look = i;
        scenes.forEach((s, k) => s.classList.toggle('on', k === i));
        tabs.forEach((b, k) => { b.classList.toggle('on', k === i); b.setAttribute('aria-selected', k === i); b.tabIndex = k === i ? 0 : -1; });
        const tpl = tpls[i];
        const render = () => {
          if (lead) { lead.textContent = tpl ? tpl.dataset.lead : ''; lead.style.opacity = ''; }
          if (itemsBox && tpl) { itemsBox.dataset.n = tpl.dataset.n; itemsBox.replaceChildren(tpl.content.cloneNode(true)); }
          itemsBox && itemsBox.classList.remove('swap');
        };
        clearTimeout(t);
        if (first || reduce()) return render();
        itemsBox && itemsBox.classList.add('swap'); if (lead) lead.style.opacity = 0; t = setTimeout(render, 280);
      };
      this._off.push(() => clearTimeout(t));
      tabs.forEach((b, k) => {
        b.onclick = () => set(k);
        b.onkeydown = e => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); set(look + (e.key === 'ArrowRight' ? 1 : -1)); tabs[look].focus(); } };
      });
      const box = $('.mc-court', root);
      let sw = null;
      this.on(box, 'pointerdown', e => { if (e.pointerType !== 'mouse' && !e.target.closest('.mc-looks')) sw = { x: e.clientX, y: e.clientY }; });
      this.on(box, 'pointerup', e => { if (!sw) return; const dx = e.clientX - sw.x, dy = e.clientY - sw.y; sw = null; if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.4) set(look + (dx < 0 ? 1 : -1)); });
      this.on(box, 'pointercancel', () => sw = null);
      set(0);
    }
  }

  /* ================= card 4: the house ================= */
  class House extends Card {
    setup() {
      const root = this;
      this.pauseOff(root, [$('.mc-house__ring svg', root)]);
      if (root.dataset.style !== 'codes') return;
      const codes = $$('.mc-code', root), desc = $('[data-mc-codedesc]', root);
      // Phones: every code's name and words already sit in one shared cell (sections/mc-house.liquid); only the chosen
      // one shows, sliding in from the side it was chosen from (--dir), while the one before slides out the other way.
      const words = desc ? [...desc.children] : [];
      let current = -1, outT = 0;
      const set = b => {
        const i = codes.indexOf(b);
        if (i < 0 || i === current) return;
        root.dataset.code = b.dataset.code;
        if (words.length && current >= 0) {
          desc.style.setProperty('--dir', i > current ? 1 : -1);
          void desc.offsetWidth; // the hidden words take their side before the chosen one moves
        }
        codes.forEach((c, k) => { const on = k === i; c.classList.toggle('on', on); c.setAttribute('aria-selected', on); c.tabIndex = on ? 0 : -1; });
        words.forEach((w, k) => { const on = k === i; w.classList.toggle('out', k === current && !on); w.classList.toggle('on', on); w.setAttribute('aria-hidden', String(!on)); });
        clearTimeout(outT); outT = setTimeout(() => words.forEach(w => w.classList.remove('out')), 480);
        if (desc && !words.length) desc.innerHTML = $('.mc-code__x > span', b)?.innerHTML || '';
        current = i;
      };
      this._off.push(() => clearTimeout(outT));
      codes.forEach((c, k) => {
        c.onclick = () => set(c);
        c.onkeydown = e => { if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return; const n = codes[k + (e.key === 'ArrowRight' ? 1 : -1)]; if (n) { e.preventDefault(); set(n); n.focus(); } };
      });
      // Phones: a sideways swipe across the card turns to the next or previous code; vertical scrolling is untouched.
      const narrow = matchMedia('(max-width: 699px)'), surface = $('.mc-house', root);
      let sw = null;
      if (surface) {
        this.on(surface, 'pointerdown', e => { sw = narrow.matches && e.pointerType !== 'mouse' && !e.target.closest('a') ? { x: e.clientX, y: e.clientY } : null; });
        this.on(surface, 'pointerup', e => { if (!sw) return; const dx = e.clientX - sw.x, dy = e.clientY - sw.y; sw = null; if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5) { const n = codes[current + (dx < 0 ? 1 : -1)]; if (n) set(n); } });
        this.on(surface, 'pointercancel', () => sw = null);
      }
      if (codes[0]) set(codes.find(c => c.classList.contains('on')) || codes[0]);
      // Wider screens: a chosen code opens its words in place. The list keeps the height it has with the longest code
      // open, so choosing another never changes the card's height: the stack re-measures and re-pins the cards on
      // every resize, which showed as a jolt mid-animation (Safari on a Mac most of all).
      const list = $('.mc-codes', root);
      const hold = () => {
        if (!list) return;
        list.style.minHeight = '';
        if (narrow.matches) return;
        const spans = codes.map(c => $('.mc-code__x > span', c));
        const open = spans[current], tallest = Math.max(0, ...spans.map(s => (s ? s.scrollHeight : 0)));
        if (!open) return;
        list.style.minHeight = Math.ceil(list.offsetHeight - open.offsetHeight + tallest) + 'px';
      };
      let holdT = 0, lastW = 0;
      const rehold = () => { clearTimeout(holdT); holdT = setTimeout(hold, 650); };
      this._off.push(() => clearTimeout(holdT));
      this.observe(ResizeObserver, () => { const w = root.offsetWidth; if (w !== lastW) { lastW = w; rehold(); } }, root);
      document.fonts?.ready.then(rehold);
    }
  }

  customElements.get('mc-piece') || customElements.define('mc-piece', Piece);
  customElements.get('mc-details') || customElements.define('mc-details', Details);
  customElements.get('mc-figure') || customElements.define('mc-figure', Figure);
  customElements.get('mc-house') || customElements.define('mc-house', House);
  window.mcChapter = { Stack };
})();
